/**
 * Cash / treasury helpers — Saudi SAR defaults, denominations, payment methods.
 * Used by caisse module pages and exports.
 */
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { formatPreviewPaymentMethod } from '../i18n/documentPreviewLabels';
import {
  formatBrandMoney,
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandLocale,
  normalizeBrandCurrency,
  SAUDI_CURRENCY_CODE,
} from './brandCurrency';

export { getBrandLocale };

/** Saudi banknotes (SAMA circulation). */
export const SAUDI_BANKNOTES = [
  { value: 500, label: '500 ر.س', type: 'banknote' },
  { value: 100, label: '100 ر.س', type: 'banknote' },
  { value: 50, label: '50 ر.س', type: 'banknote' },
  { value: 10, label: '10 ر.س', type: 'banknote' },
  { value: 5, label: '5 ر.س', type: 'banknote' },
  { value: 1, label: '1 ر.س', type: 'banknote' },
];

/** Saudi coins (halala denominations). */
export const SAUDI_COINS = [
  { value: 2, label: '2 ريال', type: 'coin' },
  { value: 1, label: '1 ريال', type: 'coin' },
  { value: 0.5, label: '50 هللة', type: 'coin' },
  { value: 0.25, label: '25 هللة', type: 'coin' },
  { value: 0.1, label: '10 هللة', type: 'coin' },
  { value: 0.05, label: '5 هللة', type: 'coin' },
  { value: 0.01, label: '1 هللة', type: 'coin' },
];

/** Legacy Algerian denominations (non-Saudi builds only). */
export const LEGACY_ALGERIAN_DENOMINATIONS = [
  { value: 2000, label: '2000 DA', type: 'banknote' },
  { value: 1000, label: '1000 DA', type: 'banknote' },
  { value: 500, label: '500 DA', type: 'banknote' },
  { value: 200, label: '200 DA', type: 'banknote' },
  { value: 100, label: '100 DA', type: 'banknote' },
];

export function getBrandCashDenominations() {
  if (isSaudiBrand()) {
    return { banknotes: SAUDI_BANKNOTES, coins: SAUDI_COINS };
  }
  return { banknotes: LEGACY_ALGERIAN_DENOMINATIONS, coins: [] };
}

export function getDefaultEnabledDenominations() {
  const { banknotes, coins } = getBrandCashDenominations();
  return [...banknotes, ...coins].map((d) => d.value);
}

/** Default treasury thresholds — SAR for Saudi, DZD for legacy. */
export function buildDefaultTreasuryConfig() {
  const saudi = isSaudiBrand();
  return {
    defaultCurrency: getBrandDefaultCurrency(),
    maxCashAmount: saudi ? 500000 : 5000000,
    minCashAmount: saudi ? 5000 : 100000,
    requireApprovalAbove: saudi ? 50000 : 1000000,
    autoClosureTime: '18:00',
    requireDualApproval: true,
    maxDiscrepancy: saudi ? 50 : 5000,
    alertOnDiscrepancy: true,
    maxTransferAmount: saudi ? 1000000 : 10000000,
    requireApprovalForTransfers: true,
    autoNotifyTransfers: true,
    transferApprovalLimit: saudi ? 25000 : 500000,
    voucherPrefix: saudi ? 'سند' : 'BON',
    transferPrefix: 'TRF',
    closurePrefix: 'CLS',
    reconciliationPrefix: 'RAP',
    voucherNumbering: 'auto',
    requireSignature: true,
    reconciliationFrequency: 'weekly',
    autoReconciliation: false,
    alertOnMismatch: true,
    notifyOnClosure: true,
    notifyOnTransfer: true,
    notifyOnLargeTransaction: true,
    emailNotifications: true,
    requirePinForOperations: false,
    sessionTimeout: 30,
    logAllTransactions: true,
    showBalanceHistory: true,
    defaultView: 'journal',
    itemsPerPage: 50,
    enabledDenominations: getDefaultEnabledDenominations(),
    highCashAlertThreshold: saudi ? 200000 : 3000000,
  };
}

export function normalizeTreasuryConfig(raw) {
  const defaults = buildDefaultTreasuryConfig();
  const merged = { ...defaults, ...(raw || {}) };
  merged.defaultCurrency = normalizeBrandCurrency(merged.defaultCurrency);
  if (isSaudiBrand()) {
    merged.enabledDenominations = (merged.enabledDenominations || defaults.enabledDenominations)
      .filter((v) => getDefaultEnabledDenominations().includes(v));
    if (!merged.enabledDenominations.length) {
      merged.enabledDenominations = defaults.enabledDenominations;
    }
  }
  return merged;
}

/** Map treasury config prefixes into settings.configuration.entityPrefixes keys. */
export function buildTreasuryEntityPrefixes(config) {
  const c = normalizeTreasuryConfig(config);
  return {
    voucher: c.voucherPrefix,
    transfer: c.transferPrefix,
    closure: c.closurePrefix,
    caisseReconciliation: c.reconciliationPrefix,
  };
}

/** Saudi payment methods (no CCP / Baridimob). */
export function getBrandPaymentMethods() {
  if (isSaudiBrand()) {
    return [
      { value: 'cash', label: 'نقداً' },
      { value: 'mada', label: 'مدى' },
      { value: 'sarie', label: 'سريع (SARIE)' },
      { value: 'check', label: 'شيك' },
      { value: 'bank_transfer', label: 'تحويل بنكي' },
    ];
  }
  return [
    { value: 'cash', label: formatPreviewPaymentMethod('cash') },
    { value: 'check', label: formatPreviewPaymentMethod('cheque') },
    { value: 'transfer', label: formatPreviewPaymentMethod('bank_transfer') },
    { value: 'card', label: formatPreviewPaymentMethod('card') },
  ];
}

/** Consistent monetary display across treasury pages (ر.س / DA). */
export function formatTreasuryAmount(amount, showSymbol = true) {
  return formatBrandMoney(amount, showSymbol);
}

export function formatTreasuryChartTick(value) {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) {
    const suffix = getBrandCurrencySuffix();
    return `${(n / 1_000_000).toFixed(1)}M ${suffix}`;
  }
  if (Math.abs(n) >= 10_000) {
    const suffix = getBrandCurrencySuffix();
    return `${(n / 1_000).toFixed(0)}K ${suffix}`;
  }
  return formatBrandMoney(n);
}

export function getTreasuryExportCurrencyCode() {
  return isSaudiBrand() ? SAUDI_CURRENCY_CODE : 'DZD';
}

const MONEY_EXPORT_KEYS = new Set([
  'balance', 'credit', 'debit', 'solde', 'montant', 'total',
  'soldeOuverture', 'totalEntrees', 'totalSorties', 'soldeFermeture',
  'soldeReel', 'ecart', 'soldeSysteme', 'soldePhysique',
]);

/** Format monetary columns for CSV/Excel/PDF exports. */
export function formatTreasuryExportRows(rows) {
  return (rows || []).map((row) => {
    const formatted = { ...row };
    for (const [key, val] of Object.entries(formatted)) {
      if (MONEY_EXPORT_KEYS.has(key) && val != null && val !== '') {
        formatted[key] = formatBrandMoney(val);
      }
    }
    if (formatted.currency == null || formatted.currency === '') {
      formatted.currency = getTreasuryExportCurrencyCode();
    }
    return formatted;
  });
}

/** Recharts tooltip formatter. */
export function treasuryChartTooltipFormatter(value) {
  return [formatBrandMoney(value), ''];
}

export { formatChartDate as formatTreasuryChartDate } from './chartFormatters';

/** Chart.js tooltip label callback. */
export function treasuryChartJsTooltipLabel(context) {
  const label = context.dataset?.label || '';
  const value = context.parsed?.y ?? context.parsed ?? context.raw ?? 0;
  return `${label}: ${formatBrandMoney(value)}`;
}
